import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { MapContainer, TileLayer, Circle, Polygon, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

/**
 * Map component for drawing geofences
 */
const GeofenceDrawer = ({ geofenceType, center, onGeofenceDrawn, currentGeofence }) => {
  const [isDrawing, setIsDrawing] = useState(false);
  const [circleCenter, setCircleCenter] = useState(null);
  const [circleRadius, setCircleRadius] = useState(100);
  const [polygonPoints, setPolygonPoints] = useState([]);
  const isInitialMount = useRef(true);

  // Update map when geofence type changes (but skip initial mount to preserve loaded geofence in edit mode)
  useEffect(() => {
    // Don't clear on initial mount - this preserves the loaded geofence in edit mode
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    setCircleCenter(null);
    setPolygonPoints([]);
    setIsDrawing(false);
  }, [geofenceType]);

  // Load existing geofence if editing
  useEffect(() => {
    if (currentGeofence && currentGeofence.area) {
      const area = currentGeofence.area;
      if (area.type === 'Circle') {
        // GeoJSON coordinates are [lon, lat], but Leaflet expects [lat, lon]
        setCircleCenter([area.coordinates[1], area.coordinates[0]]);
        setCircleRadius(area.radius);
      } else if (area.type === 'Polygon') {
        // Convert GeoJSON [lon, lat] to Leaflet [lat, lon]
        const coords = area.coordinates[0].map(coord => [coord[1], coord[0]]);
        setPolygonPoints(coords);
      }
    }
  }, [currentGeofence]);

  const MapEvents = () => {
    useMapEvents({
      click(e) {
        if (geofenceType === 'Circle' && !circleCenter) {
          // First click sets circle center
          setCircleCenter([e.latlng.lat, e.latlng.lng]);
          setIsDrawing(true);

          // Notify parent with initial circle
          onGeofenceDrawn({
            type: 'Circle',
            coordinates: [e.latlng.lng, e.latlng.lat],
            radius: circleRadius
          });
        } else if (geofenceType === 'Polygon') {
          // Add point to polygon
          const newPoints = [...polygonPoints, [e.latlng.lat, e.latlng.lng]];
          setPolygonPoints(newPoints);

          if (newPoints.length >= 3) {
            // Convert to GeoJSON format (lon, lat)
            const coordinates = [newPoints.map(point => [point[1], point[0]])];
            onGeofenceDrawn({
              type: 'Polygon',
              coordinates: coordinates
            });
          }
        }
      }
    });
    return null;
  };

  // Update circle when radius changes
  useEffect(() => {
    if (circleCenter && geofenceType === 'Circle') {
      onGeofenceDrawn({
        type: 'Circle',
        coordinates: [circleCenter[1], circleCenter[0]],
        radius: circleRadius
      });
    }
  }, [circleRadius, circleCenter, geofenceType]);

  const handleClearDrawing = () => {
    setCircleCenter(null);
    setPolygonPoints([]);
    setIsDrawing(false);
    onGeofenceDrawn(null);
  };

  return (
    <div className="space-y-2">
      <MapContainer
        center={center}
        zoom={13}
        style={{ height: '400px', width: '100%' }}
        className="rounded-lg border-2 border-gray-300"
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        <MapEvents />

        {/* Vehicle marker */}
        <Marker position={center} />

        {/* Circle geofence */}
        {geofenceType === 'Circle' && circleCenter && (
          <Circle
            center={circleCenter}
            radius={circleRadius}
            pathOptions={{ color: 'blue', fillColor: 'blue', fillOpacity: 0.2 }}
          />
        )}

        {/* Polygon geofence */}
        {geofenceType === 'Polygon' && polygonPoints.length >= 3 && (
          <Polygon
            positions={polygonPoints}
            pathOptions={{ color: 'red', fillColor: 'red', fillOpacity: 0.2 }}
          />
        )}
      </MapContainer>

      {/* Drawing controls */}
      {geofenceType === 'Circle' && circleCenter && (
        <div className="flex items-center space-x-2 mt-2">
          <label className="text-sm font-medium text-gray-700">
            Radius (meters):
          </label>
          <input
            type="range"
            min="50"
            max="5000"
            step="50"
            value={circleRadius}
            onChange={(e) => setCircleRadius(parseInt(e.target.value))}
            className="flex-1"
          />
          <span className="text-sm text-gray-600 min-w-[80px]">{circleRadius}m</span>
        </div>
      )}

      {((geofenceType === 'Circle' && circleCenter) || (geofenceType === 'Polygon' && polygonPoints.length > 0)) && (
        <button
          type="button"
          onClick={handleClearDrawing}
          className="w-full bg-red-100 text-red-700 px-3 py-2 rounded-md hover:bg-red-200 transition-colors text-sm"
        >
          Clear Drawing
        </button>
      )}

      <div className="text-xs text-gray-500 mt-2">
        {geofenceType === 'Circle' && !circleCenter && '📍 Click on the map to set the center of your geofence'}
        {geofenceType === 'Circle' && circleCenter && '✅ Adjust the radius using the slider above'}
        {geofenceType === 'Polygon' && polygonPoints.length < 3 && `📍 Click ${3 - polygonPoints.length} more point(s) to create a polygon`}
        {geofenceType === 'Polygon' && polygonPoints.length >= 3 && '✅ Polygon created! Click more points to adjust or clear to start over'}
      </div>
    </div>
  );
};

/**
 * Geofence Creation/Edit Form Component
 * Allows users to create or edit virtual boundaries around locations with map-based drawing
 */
const GeofenceForm = ({ vehicle, onClose, onSuccess, currentUser, currentGeofence = null, isEditing = false }) => {
  const [formData, setFormData] = useState({
    name: currentGeofence?.name || '',
    description: currentGeofence?.description || '',
    notification_email: currentGeofence?.notification_email || currentUser?.email || '',
    is_active: currentGeofence?.is_active ?? true,
    notify_on_enter: currentGeofence?.notify_on_enter ?? true,
    notify_on_exit: currentGeofence?.notify_on_exit ?? true,
    geofence_type: currentGeofence?.area?.type || 'Circle',
    auto_action_on_enter: currentGeofence?.auto_action_on_enter || '',
    auto_action_on_exit: currentGeofence?.auto_action_on_exit || ''
  });

  const [geofenceArea, setGeofenceArea] = useState(currentGeofence?.area || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState('');

  // Fetch available geofence presets
  useEffect(() => {
    const fetchPresets = async () => {
      try {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const response = await axios.get(`${API}/geofences/presets`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setPresets(response.data.presets || []);
      } catch (err) {
        console.error('Failed to fetch geofence presets:', err);
        // Don't show error to user - presets are optional
      }
    };
    fetchPresets();
  }, []);

  // Get vehicle's last known location or default to a center point
  const getVehicleCenter = () => {
    if (vehicle.last_location) {
      return [vehicle.last_location.latitude, vehicle.last_location.longitude];
    }
    // Default to Belize City coordinates if no location
    return [17.2534, -88.7713];
  };

  // Handle preset selection
  const handlePresetSelect = (presetId) => {
    setSelectedPreset(presetId);

    if (presetId) {
      const preset = presets.find(p => p.id === presetId);
      if (preset) {
        // Apply preset area
        setGeofenceArea(preset.area);
        // Set geofence type to Polygon (presets are always polygons)
        setFormData(prev => ({
          ...prev,
          geofence_type: 'Polygon',
          name: prev.name || preset.name, // Suggest preset name if user hasn't entered one
          description: prev.description || preset.description || ''
        }));
      }
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGeofenceDrawn = (area) => {
    setGeofenceArea(area);
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Please enter a name for the geofence');
      return false;
    }

    if (!formData.notification_email.trim()) {
      setError('Please enter an email address for notifications');
      return false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.notification_email)) {
      setError('Please enter a valid email address');
      return false;
    }

    if (!geofenceArea) {
      setError('Please draw a geofence on the map');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      const geofenceData = {
        vehicle_id: vehicle.id,
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        area: geofenceArea,
        notification_email: formData.notification_email.trim(),
        is_active: formData.is_active,
        notify_on_enter: formData.notify_on_enter,
        notify_on_exit: formData.notify_on_exit,
        auto_action_on_enter: formData.auto_action_on_enter || null,
        auto_action_on_exit: formData.auto_action_on_exit || null
      };

      let response;
      if (isEditing && currentGeofence) {
        // Update existing geofence (don't send vehicle_id in update)
        const { vehicle_id, ...updateData } = geofenceData;
        response = await axios.put(
          `${API}/geofences/${currentGeofence.id}`,
          updateData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      } else {
        // Create new geofence
        response = await axios.post(
          `${API}/geofences`,
          geofenceData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      }

      if (response.status === 200 || response.status === 201) {
        onSuccess && onSuccess(response.data);
        onClose();
      }
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to ${isEditing ? 'update' : 'create'} geofence. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-800">
            {isEditing ? 'Edit Geofence' : 'Create Geofence'} for {vehicle.year} {vehicle.make} {vehicle.model}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Geofence Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Geofence Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Home, Work, School, Parking Lot"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description (Optional)
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Add any additional details about this geofence"
              rows="2"
            />
          </div>

          {/* Notification Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notification Email *
            </label>
            <input
              type="email"
              value={formData.notification_email}
              onChange={(e) => handleInputChange('notification_email', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="email@example.com"
            />
          </div>

          {/* Geofence Preset Selector */}
          {presets.length > 0 && !isEditing && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Use Preset Area (Optional)
              </label>
              <select
                value={selectedPreset}
                onChange={(e) => handlePresetSelect(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-blue-50"
              >
                <option value="">-- Select a preset or draw custom area --</option>
                {presets.map((preset) => (
                  <option key={preset.id} value={preset.id}>
                    {preset.name} {preset.description && `- ${preset.description}`}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Presets are predefined areas created by administrators for commonly used locations.
              </p>
            </div>
          )}

          {/* Geofence Type Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Geofence Type
            </label>
            <select
              value={formData.geofence_type}
              onChange={(e) => {
                handleInputChange('geofence_type', e.target.value);
                // Clear preset selection if user manually changes type
                setSelectedPreset('');
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="Circle">Circle (recommended for most uses)</option>
              <option value="Polygon">Custom Polygon (for complex shapes)</option>
            </select>
          </div>

          {/* Map for drawing geofence */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Draw Geofence on Map *
            </label>
            <GeofenceDrawer
              geofenceType={formData.geofence_type}
              center={getVehicleCenter()}
              onGeofenceDrawn={handleGeofenceDrawn}
              currentGeofence={currentGeofence || (geofenceArea ? { area: geofenceArea } : null)}
            />
          </div>

          {/* Notification Settings */}
          <div className="bg-gray-50 p-4 rounded-lg space-y-2">
            <h4 className="font-medium text-gray-800 mb-2">Notification Settings</h4>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.notify_on_enter}
                onChange={(e) => handleInputChange('notify_on_enter', e.target.checked)}
                className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700">
                Notify when vehicle enters this geofence
              </span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.notify_on_exit}
                onChange={(e) => handleInputChange('notify_on_exit', e.target.checked)}
                className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700">
                Notify when vehicle exits this geofence
              </span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => handleInputChange('is_active', e.target.checked)}
                className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700">
                Activate geofence immediately
              </span>
            </label>
          </div>

          {/* Automatic Actions - Only show if vehicle has remote shutdown */}
          {vehicle.has_remote_shutdown && (
            <div className="bg-orange-50 p-4 rounded-lg space-y-3 border border-orange-200">
              <h4 className="font-medium text-gray-800 mb-2 flex items-center">
                <span className="mr-2">⚡</span>
                Automatic Actions
              </h4>
              <p className="text-xs text-orange-700 mb-3">
                Configure automatic actions when the vehicle enters or exits this geofence. These actions will execute automatically without user intervention.
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Action when vehicle ENTERS geofence
                </label>
                <select
                  value={formData.auto_action_on_enter}
                  onChange={(e) => handleInputChange('auto_action_on_enter', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">No automatic action</option>
                  <option value="shutdown_vehicle">🔴 Shutdown Vehicle Engine</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Action when vehicle EXITS geofence
                </label>
                <select
                  value={formData.auto_action_on_exit}
                  onChange={(e) => handleInputChange('auto_action_on_exit', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">No automatic action</option>
                  <option value="shutdown_vehicle">🔴 Shutdown Vehicle Engine</option>
                </select>
              </div>

              <div className="bg-white border border-orange-300 rounded p-3 text-xs text-orange-800">
                <strong>⚠️ Warning:</strong> The vehicle engine will automatically shutdown when the configured event occurs. Use this feature with caution.
              </div>
            </div>
          )}

          {/* Submit Buttons */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`flex-1 px-4 py-2 rounded-md text-white transition-colors ${
                loading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {loading ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update Geofence' : 'Create Geofence')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GeofenceForm;
